import {jsx} from 'panel';

import {Placement} from 'common/components/tooltip/types';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {DateRangePickerEl} from 'common/widgets/date-range-picker-new';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DateRangeType, DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {DateRangePickerApplyEvent} from 'common/widgets/date-range-picker-new/types';
import {getDateRangeLabel} from 'common/widgets/date-range-picker-new/util';
import {getCurrentMoment} from 'common/util/date';
import {PropertyFilterDateRange} from 'common/widgets/date-range-picker/models/property-filter-date-range';
import {renderDateRangeTooltip} from 'common/widgets/date-range-preset-bar/date-range-tooltip';
import {currentProjectStore} from 'common/report/stores/project-details';
import {TrackingUsageContext} from 'common/report/tracking-setup';

import {FilterValueProps} from '../types';

function getSelectedDateLabel(dateRange: DateRange) {
  return getDateRangeLabel(
    dateRange,
    null,
    null /* project as of date; we set this to "null" because we're trialing a change where we show the project-as-of-date in the tooltip, not the label */,
    getCurrentMoment({localized: true}),
    `none`,
  );
}

export function FilterDateValue({
  filter,
  isActive,
  onSelectMenuChange,
  updateEntry,
  setActiveEntry,
  readonly,
  hasNewUI,
}: FilterValueProps) {
  /**
   * All possible date filters are expressed as a relative or absolute date selection
   * we express date filters in terms of these 2 primitives
   */
  const selectedDateAttrs = new DateRange(filter.filterValue as Partial<DateRange>).getAttributes();
  switch (selectedDateAttrs.type) {
    case DateRangeType.NotOn:
      selectedDateAttrs.type = DateRangeType.On;
      break;
    case DateRangeType.NotBetween:
      selectedDateAttrs.type = DateRangeType.Between;
      break;
    case DateRangeType.RelativeFuture:
    case DateRangeType.RelativeBefore:
    case DateRangeType.NotRelativeAfter:
      selectedDateAttrs.type = DateRangeType.RelativeAfter;
      break;
    case DateRangeType.Before:
      selectedDateAttrs.type = DateRangeType.On;
      selectedDateAttrs.from = selectedDateAttrs.to;
      break;
  }
  const selectedDate = new PropertyFilterDateRange(selectedDateAttrs);

  /**
   * TODO: date filters can all be expressed with the following operators:
   *
   * ==, !=, >=, ∋, ∌, <=
   * = (in the last, on)
   * != (not in the last, not on)
   * > (in the next, since)
   * ∋ (between)
   * ∌ (not between)
   * < (before the last, ending)
   *
   * These require additional changes to the API:
   *  - [!= last x days] not in the last: translates to not between
   *  - [< date] before date: translates to "ending at date"
   */
  const handleDatePickerChange = (dateRange: DateRange) => {
    const {filterOperator} = filter;
    if (filterOperator === DateRangeType.Before) {
      dateRange.from = null;
    }

    const updatedFilter = filter.clonePropertyFilter({
      filterValue: filter.createDateRange({
        ...dateRange.getAttributes(),
        type: filter.filterOperator as DateRangeType,
      }),
    });

    updateEntry(updatedFilter);
  };

  return (
    <div sel='.filter-value-select'>
      <SelectEl
        class={{
          'is-placeholder': !filter.isValid(),
        }}
        attrs={{
          elref: `filterValueSelect`,
          open: isActive,
          theme: hasNewUI ? SelectTheme.Tertiary : SelectTheme.Select,
          'never-show-caret': true,
          readonly,
          'selected-option': filter.isValid() ? getSelectedDateLabel(selectedDate) : `Select value…`,
        }}
        on={{
          menuChange: (ev) => onSelectMenuChange(ev),
        }}
      >
        {isActive && (
          <DateRangePickerEl
            attrs={{
              elref: `filterDateRangePicker`,
              'single-picker-mode': true,
              'display-context': DisplayContext.PropertyFilter,
              slot: `body`,
              'date-range': filter.isValid() && selectedDate.getAttributes(),
              'tracked-use-case': TrackingUsageContext.QueryBuilder,
            }}
            on={{
              apply: (ev: DateRangePickerApplyEvent) => handleDatePickerChange(ev.detail.dateRange),
              cancel: () => setActiveEntry(`close`),
            }}
          />
        )}
      </SelectEl>
      {!isActive &&
        filter.isValid() &&
        renderDateRangeTooltip(filter.filterValue as DateRange, currentProjectStore, Placement.Bottom)}
    </div>
  );
}
